<template>
  <BaseDialog :is-open="isRevealed" @close="cancel">
    <div
      v-if="!productModifierGroupFormRef?.isRevealed"
      class="p-4"
    >
      <div class="mb-8">
        <div class="mb-2 flex items-center justify-between">
          <h2 class="mb-2 text-left text-background-fg/87">
            Active Modifiers
          </h2>
          <ButtonCreate
            @click="onCreateProductModifierGroup(false)"
          >
            Create Local Modifier
          </ButtonCreate>
        </div>
        <ul class="border border-background-fg/8">
          <template v-if="selectedGroupIds.length > 0">
            <template
              v-for="modifierGroupId in selectedGroupIds"
              :key="modifierGroupId"
            >
              <li
                class="flex items-center justify-between space-x-2 bg-background px-2"
              >
                <ProductModifierGroupInfoCard
                  :product-modifier-group-id="
                    modifierGroupId
                  "
                />
                <div class="flex items-center">
                  <button
                    type="button"
                    class="flex w-full items-center justify-end rounded-lg border px-1 text-xs"
                    @click="
                      onEditProductModifierGroup(
                        modifierGroupId
                      )
                    "
                  >
                    Edit
                  </button>
                </div>

                <button
                  class="flex items-center justify-center"
                  @click="emit('remove', modifierGroupId)"
                >
                  <template
                    v-if="
                      getById(modifierGroupId).value
                        ?.isShared
                    "
                  >
                    <IconMinus />
                  </template>
                  <template v-else>
                    <IconTrash />
                  </template>
                </button>
              </li>
            </template>
          </template>
          <template v-else>
            <p class="py-1 text-sm text-background-fg/87">
              No active modifiers
            </p>
          </template>
        </ul>
      </div>

      <div>
        <div class="mb-2 flex items-center justify-between">
          <h2 class="text-left text-background-fg/87">
            Shared Modifiers
          </h2>
          <ButtonCreate
            @click="onCreateProductModifierGroup(true)"
          >
            Create Shared Modifier
          </ButtonCreate>
        </div>
        <ul class="border border-background-fg/8">
          <template
            v-if="
              sharedNotSelectedModifierGroups.length > 0
            "
          >
            <template
              v-for="modifierGroup in sharedNotSelectedModifierGroups"
              :key="modifierGroup.id"
            >
              <li
                class="flex items-center justify-between space-x-2 px-2"
                :class="
                  isValueSelected(modifierGroup.id)
                    ? 'bg-primary/10'
                    : 'bg-background'
                "
              >
                <ProductModifierGroupInfoCard
                  :product-modifier-group-id="
                    modifierGroup.id
                  "
                />
                <div class="flex items-center">
                  <button
                    type="button"
                    class="flex w-full items-center justify-end rounded-lg border px-1 text-xs"
                    @click="
                      onEditProductModifierGroup(
                        modifierGroup.id
                      )
                    "
                  >
                    Edit
                  </button>
                </div>

                <button
                  class="flex items-center justify-center"
                  @click="emit('push', modifierGroup.id)"
                >
                  <IconPlus />
                </button>
              </li>
            </template>
          </template>
          <template v-else>
            <p class="py-1 text-sm text-background-fg/87">
              No shared modifiers to select
            </p>
          </template>
        </ul>
      </div>
    </div>

    <ProductModifierGroupForm
      ref="productModifierGroupFormRef"
    />
  </BaseDialog>
</template>

<script setup lang="ts">
const props = defineProps<{
  selectedGroupIds: string[]
}>()

const emit = defineEmits<{
  (event: "push", groupId: string): void
  (event: "remove", groupId: string): void
  (event: "cancel"): void
}>()

const { reveal, isRevealed, cancel } = useConfirmDialog<
  { productModifierGroupId?: string },
  string
>()

const isValueSelected = (id: string) =>
  props.selectedGroupIds.includes(id)

defineExpose({ reveal })

const { data: modifierGroups, getById } =
  useProductModifierGroups()

const productModifierGroupFormRef = useTemplateRef(
  "productModifierGroupFormRef"
)

const sharedNotSelectedModifierGroups = computed(
  () =>
    modifierGroups.value?.filter((modifierGroup) => {
      return (
        modifierGroup.isShared &&
        !props.selectedGroupIds.includes(modifierGroup.id)
      )
    }) || []
)

const onCreateProductModifierGroup = async (
  isShared = false
) => {
  if (!productModifierGroupFormRef.value) return
  const { data: newGroupId, isCanceled } =
    await productModifierGroupFormRef.value.reveal({
      ...productModifierInitialFormValues,
      isShared,
      options: productModifierInitialFormValues.options.map(
        (option) => ({ ...option })
      ),
    })
  if (isCanceled || !newGroupId) return
  emit("push", newGroupId)
}

const onEditProductModifierGroup = async (
  modifierGroupId: string
) => {
  const modifierGroup = getById(modifierGroupId).value

  if (!modifierGroup) return

  if (!productModifierGroupFormRef.value) return

  await productModifierGroupFormRef.value.reveal({
    ...modifierGroup,
    options: modifierGroup.options.map((option) => ({
      ...option,
    })),
  })
}
</script>
